import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, TextField, Typography } from '@mui/material';

import { AvatarTypes } from '../../../components/designe/app-avatar/AppAvatar';
import { AppBarWithButtons } from '../../../components/designe/app-bar-with-action-buttons/AppBarWithButtons';
import AppDocsViewer from '../../../components/designe/app-docs-viewer/AppDocsViewer';
import AppEntityAvatar, {
  AvatarSize,
} from '../../../components/designe/app-entity-avatar/AppEntityAvatar';
import AppFileContainer from '../../../components/designe/app-file-container/AppFileContainer';
import AppAutocomplete from '../../../components/designe/app-autocomplete/AppAutocomplete';
import AppFormSelectWrapper from '../../../components/designe/app-form-select-wrapper';
import AppFormTextField from '../../../components/designe/app-form-text-field';
import AppSwitchWithLabel from '../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import withRouter from '../../../hooks/with-router';
import theme from '../../../styles/theme';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { ILanguage } from '../../../types/interfaces/language';
import { IExerciseLoadFormula } from '../../../types/interfaces/exercise-load-formula';
import { getExerciseOptionUnitBracketSuffix } from '../../../utils/exercise-option-display';
import { useStyles } from './NewExcercise.styles';
import { useNewExercisePage } from './useNewExercisePage';
import AppButton from '../../../components/designe/app-button/AppButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIos';
import { ExerciseScope } from '../../../shared/constants';

function NewExercise(props) {
  const { params, exerciseId: exerciseIdProp, onBack } = props;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { classes } = useStyles(theme);
  const exerciseId =
    exerciseIdProp ??
    (params?.exerciseId !== 'new' ? params?.exerciseId : null);
  const handleBack = onBack ?? (() => navigate(-1));

  const {
    formik,
    onFilesToDeleteChanged,
    onFilesToUploadChanged,
    onMainFileChanged,
    exerciseFiles,
    isLoading,
    exercise,
    selectedFile,
    exerciseOptions,
    categories,
    mainMuscleGroups,
    equipments,
    developedSkills,
    setSelectedFile,
    onChangeFormikOption,
    languages,
    availableFormulas,
    onGenerateDescription,
    onTranslateMissing,
    isGenerateDescriptionLoading,
    isTranslateMissingLoading,
    canUseDescriptionGenerate,
    canUseTranslate,
  } = useNewExercisePage(exerciseId, {
    onSuccess: onBack,
  });

  const isPublicScope = formik.values.scope?.id === ExerciseScope.public;

  function getItem(item: IExerciseProperty | ILanguage) {
    return (
      <Box display="flex" gap={1} alignItems="center">
        <AppEntityAvatar
          entity={item}
          size={AvatarSize.s}
          type={AvatarTypes.round}
        />
        <Typography variant="text-md">
          {'name' in item ? item.name : 'title' in item && item.title}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <AppButton variant="text" onClick={handleBack}>
        <ArrowBackIcon />
        <Typography variant="h2">{t('common.exercise')}</Typography>
      </AppButton>
      <div className={classes.addExerciseWrapper}>
        <form
          onSubmit={formik.handleSubmit}
          className={classes.addExerciseForm}>
          <div className={classes.formItem}>
            <AppFileContainer
              files={exercise?.files}
              editMode={true}
              onFilesToDeleteChanged={onFilesToDeleteChanged}
              onFilesToUploadChanged={onFilesToUploadChanged}
              onMainFileChanged={onMainFileChanged}
              canSetAsMainFile={(file) =>
                String(file.mimeType || '').toLowerCase() !== 'image/gif'
              }
              mainFileId={formik.values?.avatarFileId}
            />
            {formik.errors?.['avatarFileId'] && (
              <Typography variant="body1" color="error">
                {t('formikValidation.exerciseAvatarRequired')}
              </Typography>
            )}
          </div>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="h3">{t('exercises.language')}</Typography>
          </Box>
          {formik.values.exerciseTranslations &&
            formik.values.exerciseTranslations.map((translation, index) => (
              <Box
                key={translation.languageId}
                className={classes.languageWrapper}>
                <Typography variant="h3">
                  {
                    languages.find((l) => l.id === translation.languageId)
                      ?.title
                  }
                </Typography>

                <Box display="flex" alignItems="flex-start" gap={1}>
                  <AppFormTextField
                    label={t('common.title')}
                    fullWidth
                    autoFocus={index === 0}
                    name={`exerciseTranslations[${index}].title`}
                    formik={formik}
                  />
                  {canUseDescriptionGenerate && (
                    <AppButton
                      variant="outlined"
                      size="small"
                      onClick={() => onGenerateDescription(index)}
                      disabled={
                        isGenerateDescriptionLoading ||
                        !translation?.title?.trim()
                      }
                      sx={{ mt: 1, flexShrink: 0 }}>
                      {t('exercises.generateDescription')}
                    </AppButton>
                  )}
                </Box>

                <AppFormTextField
                  label={t('common.description')}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  name={`exerciseTranslations[${index}].description`}
                  formik={formik}
                />
              </Box>
            ))}
          {typeof formik.errors?.['exerciseTranslations'] === 'string' && (
            <Typography variant="body1" color="error">
              {formik.errors?.['exerciseTranslations']}
            </Typography>
          )}

          {canUseTranslate && (
            <Box display="flex" alignItems="center" gap={1} marginY={1}>
              <AppButton
                variant="contained"
                size="small"
                onClick={() => onTranslateMissing()}
                disabled={isTranslateMissingLoading}>
                {t('exercises.addTranslations')}
              </AppButton>
            </Box>
          )}

          <Grid container spacing={1}>
            <Grid item md={6} xs={12}>
              <AppFormSelectWrapper
                label={t('exercises.mainMuscleGroup')}
                className={classes.selectorWithContent}
                options={mainMuscleGroups}
                defaultValue={mainMuscleGroups.find(
                  (e) => e.id === formik.values.mainMuscleGroup?.id,
                )}
                name="mainMuscleGroup"
                formik={formik}
                itemWrapperCallback={getItem}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={
                  mainMuscleGroups?.filter(
                    (muscle) =>
                      muscle.id !== formik.values?.mainMuscleGroup?.id,
                  ) || []
                }
                value={formik.values?.additionalMuscles || []}
                onChange={(e, value) => {
                  formik.setFieldValue('additionalMuscles', value || []);
                }}
                getOptionLabel={(option: IExerciseProperty) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {getItem(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.additionalMuscles')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
                className={classes.selectorWithContent}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppFormSelectWrapper
                label={t('exercises.equipment')}
                className={classes.selectorWithContent}
                options={equipments}
                defaultValue={equipments.find(
                  (e) => e.id === formik.values.equipment?.id,
                )}
                name="equipment"
                formik={formik}
                itemWrapperCallback={getItem}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={
                  equipments?.filter(
                    (eq) => eq.id !== formik.values?.equipment?.id,
                  ) || []
                }
                value={formik.values?.additionalEquipments || []}
                onChange={(e, value) => {
                  formik.setFieldValue('additionalEquipments', value || []);
                }}
                getOptionLabel={(option: IExerciseProperty) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {getItem(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.additionalEquipments')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
                className={classes.selectorWithContent}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppAutocomplete
                multiple
                disableCloseOnSelect
                options={developedSkills || []}
                value={formik.values?.developedSkills || []}
                onChange={(e, value) => {
                  formik.setFieldValue('developedSkills', value || []);
                }}
                getOptionLabel={(option: IExerciseProperty) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {getItem(option)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('exercises.developedSkills')}
                    margin="dense"
                    variant="outlined"
                  />
                )}
                className={classes.selectorWithContent}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppFormSelectWrapper
                className={classes.selectorWithContent}
                label={t('exercises.category')}
                options={categories}
                defaultValue={categories.find(
                  (e) => e.id === formik.values.category?.id,
                )}
                name="category"
                formik={formik}
                itemWrapperCallback={getItem}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppFormSelectWrapper
                label={t('exercises.language')}
                className={classes.selectorWithContent}
                options={languages}
                fieldName="title"
                disableSearch
                disableClear
                defaultValue={languages.find(
                  (e) => e.id === formik.values.language?.id,
                )}
                name="language"
                formik={formik}
                itemWrapperCallback={getItem}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <AppFormSelectWrapper
                label={t('exercises.formulaForCalculationLoad')}
                options={availableFormulas}
                disabled={!availableFormulas.length}
                fieldName="name"
                disableSearch
                name="exerciseLoadFormula"
                formik={formik}
                itemWrapperCallback={(formula: IExerciseLoadFormula) => (
                  <Typography variant="text-md">
                    {formula.name || formula.nameKey}
                  </Typography>
                )}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const selectedFormula = e?.target
                    ?.value as unknown as IExerciseLoadFormula;

                  const selectedId = selectedFormula?.id
                    ? selectedFormula.id
                    : selectedFormula || null;
                  formik.setFieldValue('exerciseLoadFormula', selectedFormula);
                  formik.setFieldValue('exerciseLoadFormulaId', selectedId);
                }}
              />
            </Grid>
            <Grid item md={6} xs={12} display="flex" alignItems="center">
              <AppSwitchWithLabel
                labelPlacement="end"
                name="scope"
                value={isPublicScope}
                label={`${t('exercises.scope')}: ${t(
                  isPublicScope
                    ? 'exercises.scope_public'
                    : 'exercises.scope_private',
                )}`}
                checked={isPublicScope}
                onChange={(e) =>
                  formik.setFieldValue('scope', {
                    id: e.target.checked
                      ? ExerciseScope.public
                      : ExerciseScope.private,
                  })
                }
              />
            </Grid>
            <Grid item md={6} xs={12} display="flex" alignItems="center">
              <AppSwitchWithLabel
                labelPlacement="end"
                name="canCopy"
                value={!!formik.values.canCopy}
                label={t('exercises.canCopy')}
                checked={formik.values?.canCopy}
                onChange={(e) =>
                  formik.setFieldValue('canCopy', e.target.checked)
                }></AppSwitchWithLabel>
            </Grid>
          </Grid>
          <div className={classes.formItem}>
            <div className={classes.title}>{t('exercises.options')}</div>
            <div className={classes.optionsContainer}>
              <Grid container>
                <Grid item md={4} xs={6} lg={2}>
                  <AppSwitchWithLabel
                    labelPlacement="end"
                    name="splitBySide"
                    label={t('exercises.splitBySide')}
                    value={formik.values?.splitBySide}
                    checked={formik.values?.splitBySide}
                    onChange={(e) =>
                      formik.setFieldValue('splitBySide', e.target.checked)
                    }
                  />
                </Grid>
                {exerciseOptions?.map((option) => (
                  <Grid item md={4} xs={6} lg={2} key={option.id}>
                    <AppSwitchWithLabel
                      labelPlacement="end"
                      name="options"
                      value={!!option.id}
                      label={`${option.name || option.nameKey || ''}${getExerciseOptionUnitBracketSuffix(option)}`}
                      checked={
                        !!formik.values?.options?.find((id) => id === option.id)
                      }
                      onChange={(e) =>
                        onChangeFormikOption(e, option)
                      }></AppSwitchWithLabel>
                  </Grid>
                ))}
              </Grid>
            </div>
            {formik.errors?.['options'] && (
              <Typography variant="body1" color="error">
                {formik.errors?.['options']}
              </Typography>
            )}
          </div>

          <div className={classes.buttonWrapper}>
            <AppBarWithButtons loading={isLoading} onCancelClick={handleBack} />
          </div>
        </form>

        {selectedFile && (
          <AppDocsViewer
            files={exerciseFiles}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
          />
        )}
      </div>
    </Box>
  );
}

export default withRouter(NewExercise);
